import moment from "moment";
import React, { useCallback, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  Button,
  Col,
  Dropdown,
  Icon,
  Input,
  Menu,
  notification,
  Row,
  Table,
} from "antd";

import { Creators as SupplementActions } from "../../store/ducks/supplement";
import { Creators as SupplierActions } from "../../store/ducks/supplier";

import DrawerSupplement from "../../components/drawers/supplement";
import DrawerSupplementDetails from "../../components/drawers/supplementDetails";
import ButtonStandard from "../../components/utils/button";
import MenuIcon from "../../components/utils/table/icons/menu";
import { Container as MenuContainer } from "../../components/utils/table/menu/styles";
import TagStatus from "../../components/utils/tagStatus";
import { numberMask } from "../../services/helpersMethodsService";
import { transformCamelCaseToSnakeCase } from "../../utils/stringUtils";
import { getColumnDateSearchProps } from "../../utils/tableUtils";
import { CardCustom, Title } from "./styles";
import DrawerSupplementFormulated from "../../components/drawers/supplementFormulated";
import {
  activateSupplement,
  inactivateSupplement,
} from "../../services/supplementService";

const NUTRIENT_UNITS = {
  g_kg: "g/kg",
  mg_kg: "mg/kg",
  percentage: "%",
};
function Supplement() {
  const Column = Table.Column;

  const {
    app: {
      translation,
      groupSelected: { id: groupId },
      farmSelected: { id: farmId },
      user: { isInputSupplier },
    },
    supplement: {
      isLoading,
      pageData,
      pagination: { page, size, tableFilters, tableSorters },
    },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  const expandedRows = useCallback(
    (record, index, indent, expanded) => {
      const columns = [
        {
          title: translation.supplement.table.columns.version,
          dataIndex: "version",
          key: "version",
        },
        {
          title: translation.supplement.table.columns.createdAt,
          dataIndex: "createdAt",
          key: "createdAt",
          render: (text) => (
            <span>{moment(text).format("DD/MM/YYYY HH:mm")}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.userName,
          dataIndex: "userName",
          key: "userName",
        },
        {
          title: translation.supplement.table.columns.tdn,
          dataIndex: "tdn",
          key: "tdn",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)} ${
              record.tdnUnit ? NUTRIENT_UNITS[record.tdnUnit] : ""
            }`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.cp,
          dataIndex: "cp",
          key: "cp",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)} ${
              record.cpUnit ? NUTRIENT_UNITS[record.cpUnit] : ""
            }`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.dryMatterPercentage,
          dataIndex: "dryMatterPercentage",
          key: "dryMatterPercentage",
          render: (text) => (
            <span>{`${numberMask(text || 0, false)} %MS`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.calcium,
          dataIndex: "calcium",
          key: "calcium",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)} ${
              NUTRIENT_UNITS[record.calciumUnit] ?? ""
            }`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.phosphorus,
          dataIndex: "phosphorus",
          key: "phosphorus",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)} ${
              NUTRIENT_UNITS[record.phosphorusUnit] ?? ""
            }`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.nitrogen,
          dataIndex: "nitrogen",
          key: "nitrogen",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)} ${
              NUTRIENT_UNITS[record.nitrogenUnit] ?? ""
            }`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.zinc,
          dataIndex: "zinc",
          key: "zinc",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)} ${
              NUTRIENT_UNITS[record.zincUnit] ?? ""
            }`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.sodium,
          dataIndex: "sodium",
          key: "sodium",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)} ${
              NUTRIENT_UNITS[record.sodiumUnit] ?? ""
            }`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.sulfur,
          dataIndex: "sulfur",
          key: "sulfur",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)} ${
              NUTRIENT_UNITS[record.sulfurUnit] ?? ""
            }`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.potassium,
          dataIndex: "potassium",
          key: "potassium",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)} ${
              NUTRIENT_UNITS[record.potassiumUnit] ?? ""
            }`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.fda,
          dataIndex: "fda",
          key: "fda",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)}`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.lignin,
          dataIndex: "lignin",
          key: "lignin",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)}`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.ee,
          dataIndex: "ee",
          key: "ee",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)}`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.fdn,
          dataIndex: "fdn",
          key: "fdn",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)}`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.pdr,
          dataIndex: "pdr",
          key: "pdr",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)}`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.pndr,
          dataIndex: "pndr",
          key: "pndr",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)}`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.em,
          dataIndex: "em",
          key: "em",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)}`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.fef,
          dataIndex: "fef",
          key: "fef",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)}`}</span>
          ),
        },
        {
          title: translation.supplement.table.columns.fdnfe,
          dataIndex: "fdnfe",
          key: "fdnfe",
          render: (text, record) => (
            <span>{`${numberMask(text || 0, false)}`}</span>
          ),
        },
      ];

      return (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={record.versions}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      );
    },
    [
      translation.supplement.table.columns.calcium,
      translation.supplement.table.columns.cp,
      translation.supplement.table.columns.createdAt,
      translation.supplement.table.columns.dryMatterPercentage,
      translation.supplement.table.columns.ee,
      translation.supplement.table.columns.em,
      translation.supplement.table.columns.fda,
      translation.supplement.table.columns.fdn,
      translation.supplement.table.columns.fdnfe,
      translation.supplement.table.columns.fef,
      translation.supplement.table.columns.lignin,
      translation.supplement.table.columns.nitrogen,
      translation.supplement.table.columns.pdr,
      translation.supplement.table.columns.phosphorus,
      translation.supplement.table.columns.pndr,
      translation.supplement.table.columns.potassium,
      translation.supplement.table.columns.sodium,
      translation.supplement.table.columns.sulfur,
      translation.supplement.table.columns.tdn,
      translation.supplement.table.columns.userName,
      translation.supplement.table.columns.version,
      translation.supplement.table.columns.zinc,
    ],
  );

  const getSuppliers = useCallback(async () => {
    dispatch(
      SupplierActions.getDropdownSuppliers(groupId, farmId, "Supplement", true),
    );
  }, [dispatch, groupId, farmId]);

  const getSortString = useCallback((sorter) => {
    let customSort = sorter;

    if (sorter && Object.entries(sorter).length > 0) {
      const { field } = sorter;

      const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;

      customSort = {
        ...customSort,
        field: fieldS,
      };
    }

    return customSort;
  }, []);

  const getSearchString = useCallback((filters) => {
    let search = "";
    if (filters && Object.entries(filters).length > 0) {
      Object.entries(filters).forEach(([field, value]) => {
        if (value && value.length > 0) {
          const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;
          if (fieldS === "status") {
            if (value.includes("I")) value.push("X");
            search =
              search === ""
                ? `${fieldS} in (${value
                    .map((o) => (o = `'${o}'`))
                    .join(", ")})`
                : `${search};${fieldS} in (${value
                    .map((o) => (o = `'${o}'`))
                    .join(", ")})`;
          } else if (fieldS === "created_at") {
            const operatorS =
              value[1] === "equal"
                ? "="
                : value[1] === "greaterThan"
                  ? ">"
                  : value[1] === "greaterOrEqualThan"
                    ? ">="
                    : value[1] === "lessThan"
                      ? "<"
                      : value[1] === "lessOrEqualThan"
                        ? "<="
                        : "=";

            search =
              search === ""
                ? `cast(${fieldS} as date) ${operatorS} cast('${moment(
                    value[0],
                  ).format("YYYY-MM-DD")}' as date)`
                : `${search};cast(${fieldS} as date) ${operatorS} cast('${moment(
                    value[0],
                  ).format("YYYY-MM-DD")}' as date)`;
          } else {
            search =
              search === ""
                ? `upper(${fieldS}) like upper('%25${value}%25')`
                : `${search};upper(${fieldS}) like upper('%25${value}%25')`;
          }
        }
      });
    }

    return search !== "" ? search : "";
  }, []);

  const fetchData = useCallback(
    async (
      page = 0,
      size = 10,
      sorter = {},
      filters = "",
      tableFilters = null,
      tableSorters = null,
    ) => {
      dispatch(
        SupplementActions.indexPageSupplement(
          groupId,
          farmId,
          page,
          sorter,
          filters,
          size,
          isInputSupplier,
          tableFilters,
          tableSorters,
        ),
      );
    },
    [dispatch, farmId, groupId, isInputSupplier],
  );

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          value={selectedKeys ? selectedKeys[0] : undefined}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon="search"
          size="small"
          style={{
            width: 90,
            marginRight: 8,
            background: "#684e94",
            borderColor: "none",
            border: "none",
          }}
        />
        <Button
          onClick={() => handleReset(clearFilters)}
          icon="delete"
          size="small"
          style={{ width: 90 }}
        />
      </div>
    ),
    filterIcon: (filtered) => (
      <Icon type="search" style={{ color: filtered ? "#684e94" : undefined }} />
    ),
  });

  const handleEdit = useCallback(
    (id, supplementFormulated) => {
      if (supplementFormulated) {
        dispatch(SupplementActions.showHideCreateEditSupplementFormulated(id));
      } else {
        dispatch(SupplementActions.showDrawer("edit", id));
      }
    },
    [dispatch],
  );

  const handleDetails = useCallback(
    (id) => {
      dispatch(SupplementActions.showHideSupplementDetails(id));
    },
    [dispatch],
  );

  const handleInactivateSupplement = useCallback(
    async (id) => {
      try {
        await inactivateSupplement({
          groupId,
          farmId,
          id,
        });
        await fetchData(
          page,
          size,
          tableSorters,
          getSearchString(tableFilters),
          tableFilters,
          tableSorters,
        );
        notification.success({
          message: translation.supplement.successInactivate,
        });
      } catch (error) {
        console.error("Failed to inactivate supplement:", error);
        notification.error({
          message: translation.supplement.errorInactivate,
        });
      }
    },
    [
      groupId,
      farmId,
      fetchData,
      page,
      size,
      tableSorters,
      getSearchString,
      tableFilters,
      translation.supplement.successInactivate,
      translation.supplement.errorInactivate,
    ],
  );

  const handleActivateSupplement = useCallback(
    async (id) => {
      try {
        await activateSupplement({
          groupId,
          farmId,
          id,
        });
        await fetchData(
          page,
          size,
          tableSorters,
          getSearchString(tableFilters),
          tableFilters,
          tableSorters,
        );
        notification.success({
          message: translation.supplement.successActivate,
        });
      } catch (error) {
        console.error("Failed to inactivate supplement:", error);
        notification.error({
          message: translation.supplement.errorActivate,
        });
      }
    },
    [
      groupId,
      farmId,
      fetchData,
      page,
      size,
      tableSorters,
      getSearchString,
      tableFilters,
      translation.supplement.successActivate,
      translation.supplement.errorActivate,
    ],
  );

  const menu = (record) => {
    return (
      <Menu>
        {record.status !== "F" &&
          record.status !== "Bovexo" &&
          record.status !== "I" &&
          record.status !== "Inactive" && (
            <Menu.Item
              key="1"
              onClick={() => handleEdit(record.id, record.supplementFormulated)}
            >
              {translation.table.menu.edit}
            </Menu.Item>
          )}
        <Menu.Item key="2" onClick={() => handleDetails(record.id)}>
          {translation.table.menu.details}
        </Menu.Item>
        {(record.status === "A" || record.status === "Active") && (
          <Menu.Item
            key="3"
            onClick={() => handleInactivateSupplement(record.id)}
          >
            {translation.table.menu.inactivate}
          </Menu.Item>
        )}
        {(record.status === "I" || record.status === "Inactive") && (
          <Menu.Item
            key="4"
            onClick={() => handleActivateSupplement(record.id)}
          >
            {translation.table.menu.activate}
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const handleTableChange = useCallback(
    async (pagination = null, filters = null, sorter = null) => {
      if (pagination && filters && sorter) {
        let customSort = getSortString(sorter);
        let search = getSearchString(filters);
        await fetchData(
          pagination.current,
          pagination.pageSize,
          customSort,
          search,
          filters,
          sorter,
        );
      } else {
        await fetchData(0, 10, {}, "", filters, sorter);
      }
    },
    [getSortString, getSearchString, fetchData],
  );

  useEffect(() => {
    if ((groupId, farmId)) {
      if (isInputSupplier) Promise.all([fetchData(), getSuppliers()]);
      else fetchData();
    }
  }, [farmId, fetchData, getSuppliers, groupId, isInputSupplier]);

  return (
    <CardCustom>
      <Row type="flex" justify="space-between" className="rowHeader">
        <Col span={8}>
          <Title>{translation.supplement.table.title}</Title>
        </Col>
        <Col span={16}>
          <Row type="flex" justify="end" gutter={8}>
            <Col>
              <ButtonStandard
                buttonType="typeWithoutBackground"
                onClick={() =>
                  handleTableChange(
                    { current: page, pageSize: size },
                    tableFilters,
                    tableSorters,
                  )
                }
              >
                <Icon type="reload" />
              </ButtonStandard>
            </Col>
            <Col>
              <ButtonStandard
                type="button"
                buttonType="addNew"
                onClick={() =>
                  dispatch(
                    SupplementActions.showHideCreateEditSupplementFormulated(),
                  )
                }
              >
                {translation.supplement.buttonNewSupplementFormulated}
              </ButtonStandard>
            </Col>
            <Col>
              <ButtonStandard
                type="button"
                buttonType="addNew"
                onClick={() => dispatch(SupplementActions.showDrawer("new"))}
              >
                {translation.supplement.buttonNewSupplement}
              </ButtonStandard>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row type="flex" justify="space-between">
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <Table
            loading={isLoading}
            expandedRowRender={expandedRows}
            rowKey="id"
            size="small"
            dataSource={pageData != null ? pageData.content : []}
            pagination={{
              total: pageData != null ? pageData.totalElements : 0,
              size: size != null ? size : 10,
              current: page != null ? page : 0,
              hideOnSinglePage:
                pageData !== null && Object.entries(pageData).length !== 0
                  ? pageData.totalElements > 10
                    ? false
                    : true
                  : true,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "100", "200"],
            }}
            scroll={{
              x: true,
            }}
            onChange={handleTableChange}
            style={{ width: "100%", margin: "0" }}
          >
            <Column
              title={translation.supplement.table.columns.status}
              dataIndex="status"
              key="status"
              align="left"
              filtered
              filters={[
                {
                  text: translation.status.active,
                  value: "A",
                },
                {
                  text: translation.status.bovexo,
                  value: "F",
                },
                {
                  text: translation.status.inactive,
                  value: "I",
                },
                {
                  text: translation.status.pending,
                  value: "P",
                },
              ]}
              filterMultiple
              filteredValue={tableFilters?.status || null}
              sorter
              sortOrder={
                tableSorters?.columnKey === "status" && tableSorters.order
              }
              render={(status) =>
                status === "A" || status === "Active" ? (
                  <TagStatus
                    background="#C5F1CA"
                    borderColor="#106518"
                    color="#106518"
                  >
                    {translation.status.active}
                  </TagStatus>
                ) : status === "F" || status === "Bovexo" ? (
                  <TagStatus
                    background="#F6FFC8"
                    borderColor="#A9C133"
                    color="#A9C133"
                  >
                    Bovexo
                  </TagStatus>
                ) : status === "P" || status === "Pending" ? (
                  <TagStatus
                    background="#FFDBBC"
                    borderColor="#FE8D2A"
                    color="#FE8D2A"
                  >
                    {translation.status.pending}
                  </TagStatus>
                ) : (
                  <TagStatus
                    background="#FBC7C7"
                    borderColor="#D44C4C"
                    color="#D44C4C"
                  >
                    {translation.status.inactive}
                  </TagStatus>
                )
              }
            />
            <Column
              title={translation.supplement.table.columns.description}
              dataIndex="name"
              align="left"
              key="name"
              {...getColumnSearchProps("name")}
              filteredValue={tableFilters?.name || null}
              sorter
              sortOrder={
                tableSorters?.columnKey === "name" && tableSorters.order
              }
            />
            <Column
              title={translation.supplement.table.columns.industrialName}
              dataIndex="industrialName"
              align="left"
              key="industrialName"
              {...getColumnSearchProps("industrialName")}
              filteredValue={tableFilters?.industrialName || null}
              sorter
              sortOrder={
                tableSorters?.columnKey === "industrialName" &&
                tableSorters.order
              }
            />
            <Column
              title={translation.supplement.table.columns.supplementGroupName}
              dataIndex="supplementGroupName"
              key="supplementGroupName"
              align="left"
              {...getColumnSearchProps("supplementGroupName")}
              filteredValue={tableFilters?.supplementGroupName || null}
              sorter
              sortOrder={
                tableSorters?.columnKey === "supplementGroupName" &&
                tableSorters.order
              }
            />
            <Column
              title={
                translation.supplement.table.columns.supplementSubGroupName
              }
              dataIndex="supplementSubGroupName"
              key="supplementSubGroupName"
              align="left"
              {...getColumnSearchProps("supplementSubGroupName")}
              filteredValue={tableFilters?.supplementSubGroupName || null}
              sorter
              sortOrder={
                tableSorters?.columnKey === "supplementSubGroupName" &&
                tableSorters.order
              }
            />
            <Column
              title={
                translation.supplement.table.columns.supplementPresentationName
              }
              dataIndex="supplementPresentationName"
              key="supplementPresentationName"
              align="left"
              {...getColumnSearchProps("supplementPresentationName")}
              filteredValue={tableFilters?.supplementPresentationName || null}
              sorter
              sortOrder={
                tableSorters?.columnKey === "supplementPresentationName" &&
                tableSorters.order
              }
            />
            <Column
              title={translation.supplement.table.columns.supplierName}
              dataIndex="supplierName"
              align="left"
              key="supplierName"
              {...getColumnSearchProps("supplierName")}
              filteredValue={tableFilters?.supplierName || null}
              sorter
              sortOrder={
                tableSorters?.columnKey === "supplierName" && tableSorters.order
              }
            />
            <Column
              title={translation.supplement.table.columns.createdAt}
              dataIndex="createdAt"
              align="left"
              key="createdAt"
              {...getColumnDateSearchProps(
                "createdAt",
                handleReset,
                handleSearch,
              )}
              filteredValue={tableFilters?.createdAt || null}
              sorter
              sortOrder={
                tableSorters?.columnKey === "createdAt" && tableSorters.order
              }
              render={(text, record) => (
                <span>{moment(record.createdAt).format("DD/MM/YYYY")}</span>
              )}
            />
            <Column
              title={translation.supplement.table.columns.userName}
              dataIndex="userName"
              align="left"
              key="userName"
              {...getColumnSearchProps("userName")}
              filteredValue={tableFilters?.userName || null}
              sorter
              sortOrder={
                tableSorters?.columnKey === "userName" && tableSorters.order
              }
            />
            <Column
              align="left"
              render={(text, record) => (
                <Dropdown
                  overlay={menu(record)}
                  trigger={["click"]}
                  key={record.id}
                >
                  <MenuContainer>
                    <MenuIcon />
                  </MenuContainer>
                </Dropdown>
              )}
            />
          </Table>
        </Col>
      </Row>
      <DrawerSupplement />
      <DrawerSupplementDetails />
      <DrawerSupplementFormulated />
    </CardCustom>
  );
}

export default Supplement;
